import NoDataIcon from "@assets/icons/new/no_data.svg?react";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { usePointClouds } from "@hooks/project-data/use-point-clouds";
import { useMembers } from "@hooks/use-members";
import { Grid2 } from "@mui/material";
import { PointCloudsTable } from "@pages/project-details/project-data/point-clouds/point-clouds-table";
import { TABS_CONTENT_PADDING } from "@utils/ui-utils";
import { useMemo } from "react";

/** Renders the point clouds section of the project data tab */
export function PointClouds({ projectId }: BaseProjectIdProps): JSX.Element {
  const { pointClouds, isLoading } = usePointClouds({ projectId });
  const {
    companyMembers,
    isFetchingCompanyMembers,
    projectMembers,
    isFetchingProjectMembers,
  } = useMembers();

  /** Flag that is set to true while all the data required to show the component is not yet ready */
  const isLoadingData = useMemo(() => {
    return isLoading || isFetchingProjectMembers || isFetchingCompanyMembers;
  }, [isLoading, isFetchingCompanyMembers, isFetchingProjectMembers]);

  /**
   * The content to be shown. It can be:
   * - A generic empty page if the points clouds have been fetched and the project has no point clouds
   * - The point clouds table if the project has point clouds
   */
  const content =
    pointClouds.length === 0 && !isLoading ? (
      <EmptyPage
        title="No point clouds"
        subtitle="No point clouds to show for this project."
        // TODO: Replace with proper noPointCloudsIcon
        icon={NoDataIcon}
      />
    ) : (
      <PointCloudsTable
        projectId={projectId}
        pointClouds={pointClouds}
        isLoading={isLoadingData}
        companyMembers={companyMembers}
        projectMembers={projectMembers}
      />
    );

  return (
    <Grid2
      data-testid="project-data-point-clouds-container"
      container
      sx={{
        mt: TABS_CONTENT_PADDING,
        width: "100%",
      }}
    >
      <Grid2 width="100%">{content}</Grid2>
    </Grid2>
  );
}
